import {
  Loading,
  MAutocomplete,
  MButton,
  MCheckbox,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import {
  Box,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import {
  useEquipment,
  useFactorGeneric,
  useLaboratory,
  useTypesUnits,
  useUser,
} from "@services/servicesGenericsTables";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import useFilteredProcedures from "@app/pages/samples/hook/useFilteredProcedures";
import { TYPE_METHOD_IG_GT } from "@app/pages/samples/utils/constants";
import dayjs from "dayjs";
import FileUploadGenie, { parseRptFileGenie } from "./fileUploadGenie";
import FileUploadAreaExe, { parseRptFileExe } from "./fileUploadAreaExe";
import OutData from "./outData";
import { ResultsTypes } from "@app/types/samples.types";
import OutDataAxe from "./outDataAxe";
import { lastDetector } from "./helpers";
import { useSample } from "@app/pages/samples/utils/useSample";
import { useParams } from "react-router-dom";

export const typesReports = [
  {
    label: "Reportar sólo LID",
    value: "only_lid",
  },
  {
    label: "Reportar caracterización",
    value: "report_characterization",
  },
];

const CalculationIG = () => {
  const { setValue, getValues, watch, control } = useFormContext();
  const [oldValueTypeSource, setOldValueTypeSource] = useState<string>("");
  const { userList } = useUser();
  const { typesUnitsis } = useTypesUnits();
  const {
    fetchAll: fetchFactorGeneric,
    genericFactorList,
    loading,
  } = useFactorGeneric();

  const { laboratoriesList } = useLaboratory();
  const { equipmentList } = useEquipment();
  const {
    currentCalculation,
    importFileFromRoute,
    loadingFile,
    loadingResults,
    saveResultCalculate,
  } = useAnalysis();

  const { get } = useSample();
  const { idMuestra } = useParams();
  

  const filteredCalculation = useFilteredProcedures("CAL");

  const sourceApp = watch("source_app_calculation_info");

  useEffect(() => {
    if (sourceApp && sourceApp !== oldValueTypeSource) {
      setOldValueTypeSource(sourceApp);
      setValue("calculation_date_countig_file", undefined);
      setValue("first_counting_time", undefined);
      setValue("pico_counts_341", undefined);
      setValue("pico_counts_911", undefined);
      setValue("detector_file", undefined);
      setValue("calculation_equipment_file", undefined);
      setValue("calculation_date_countig_file_exe", undefined);
      setValue("calculation_detector", undefined);
      setValue("calibration_source_certificate", undefined);
      setValue("calculation_time_count", undefined);
      setValue("calibration_efficiency_date", undefined);
      setValue("calculation_background", undefined);
      setValue("calculation_library", undefined);
    }
  }, [sourceApp]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const methodType = sourceApp || oldValueTypeSource || watch("source_app_calculation_info_previus");
  
  useEffect(() => {
    const countingEquipment = getValues("counting_equipment");
    if (countingEquipment) {
      setValue("calculation_equipment", countingEquipment);
    }
  }, [watch("counting_equipment")]);

  const isGenie = methodType === TYPE_METHOD_IG_GT.GENIE.toUpperCase();
  const codeAnalysis = getValues("typeAnalysis")?.codigo;

  const cod_instalacion = get(Number(idMuestra))?.installation?.codigo;
  const ref_cliente = getValues("client_reference");

  useEffect(() => {
    if (oldValueTypeSource)
      setValue("source_app_calculation_info_previus", oldValueTypeSource);
  }, [JSON.stringify(oldValueTypeSource)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchFactorGeneric();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const route = getValues("source_app_calculation_info_file");
    const fetchData = async (linkRoute) => {
      const response = await importFileFromRoute(linkRoute, "TO_FORM");
      if (!response) return;
      if (
        isGenie ||
        getValues("source_app_calculation_info_previus") ===
          TYPE_METHOD_IG_GT.GENIE.toUpperCase()
      ) {
        const data = parseRptFileGenie(response);
        setValue("calculation_equipment_file", data.detector);
        setValue("calculation_date_countig_file", data.date_counting);
        setValue("calculation_detector", data.geometry ? data.geometry : "");
        setValue("calibration_source_certificate", data.certification);
        setValue("calculation_time_count", data.time_count);
        setValue("calibration_efficiency_date", data.date_energy);
        setValue("calculation_background", data.fondo);
        setValue("calculation_library", data.library);
      } else {
        const lines: string[][] = parseRptFileExe(response);
        const searchKey = `${cod_instalacion}${ref_cliente}`;
        const matchingLine = lines.find(line => line[0] === searchKey);
        
        if (matchingLine) {
          setValue("first_counting_time", matchingLine[1]);
          setValue("calculation_date_countig_file_exe", matchingLine[2]);
          setValue("pico_counts_341", matchingLine[4]);
          setValue("pico_counts_911", matchingLine[5]);
          setValue("detector_file", matchingLine[3]);
        }
      }
    };
    if (currentCalculation.id && route) {
      fetchData(route);
    }
  }, [currentCalculation.id, getValues("source_app_calculation_info_file"), watch("calculation_date_countig_file")]); // eslint-disable-line react-hooks/exhaustive-deps

  const memoGeoFactor = useMemo(() => {
    const findFactor = lastDetector(
      getValues("calculation_equipment"),
      equipmentList
    )?.factor;
    return findFactor;
  }, [genericFactorList, currentCalculation.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setValue("calculation_geometric_factor", memoGeoFactor);
  }, [memoGeoFactor]); // eslint-disable-line react-hooks/exhaustive-deps

  const textTitle = `Datos del fichero ${
    isGenie ? TYPE_METHOD_IG_GT.GENIE : TYPE_METHOD_IG_GT.AREA_EXE
  }:`;

  const handleCalculate = async () => {
    const resultData: ResultsTypes = {
      result: true,
      to_reports: watch("to_report") ?? true,
    };

    await saveResultCalculate(codeAnalysis as string, resultData);
  };

  const textButtonCalculation = "Calcular";

  return (
    <>
      {currentCalculation.index !== undefined && !loading && (
        <>
          <ResponsiveFormContainer columns={3}>
            <MAutocomplete
              key="calculation_analyst"
              name="calculation_analyst"
              options={userList}
              label="Seleccione un analista"
              getOptionLabel={(option) => option.username || ""}
              validation={{ required: "El analista es requerido" }}
            />
            <MDatePicker
              key="start_date_time"
              name="start_date_time"
              pickerType="datetime"
              label="Fecha y hora de inicio Toma de muestra"
              disabled
            />
            <MDatePicker
              key="end_date_time"
              name="end_date_time"
              pickerType="datetime"
              label="Fecha y hora fin Toma de muestra"
              disabled
            />
          </ResponsiveFormContainer>

          <Grid item xs={12} my={2}>
            <Typography variant="tagText">
              {"Datos ficha del procesamiento, preparación y contaje:"}
            </Typography>
          </Grid>
          <ResponsiveFormContainer columns={3}>
            <MTextField
              key={JSON.stringify(getValues("calculation_amount_sample"))}
              label="Cantidad de muestra"
              name="calculation_amount_sample"
              type="text"
              disabled
              fullWidth
            />
            <MAutocomplete
              key={JSON.stringify(getValues("calculation_units"))}
              name="calculation_units"
              options={typesUnitsis}
              label="Unidades"
              getOptionLabel={(option) =>
                `${option.codigo} | ${option.descripcion}`
              }
              disabled
            />
            <MAutocomplete
              key={JSON.stringify(getValues("calculation_equipment"))}
              name="calculation_equipment"
              options={equipmentList}
              label="Equipos y geometrías"
              getOptionLabel={(option) =>
                `${option.codigo} - ${
                  option.is_geometry 
                    ? (option.height || option.descripcion) 
                    : option.descripcion
                }` || ""
              }
              disabled
              multiple={true}
            />
            <MTextField
              key={JSON.stringify(getValues("geometry_height"))}
              label="Altura (mm)"
              name="geometry_height"
              type="text"
              disabled
              fullWidth
            />
            <MTextField
              key={JSON.stringify(getValues("calculation_tsie_sample"))}
              name="calculation_tsie_sample"
              label="Peso Húmedo/ Seco"
              type="text"
              disabled
              fullWidth
            />
            <MTextField
              key={JSON.stringify(getValues("calculation_shared"))}
              name="calculation_shared"
              label="Compartida"
              type="text"
              disabled
              fullWidth
            />
          </ResponsiveFormContainer>
          <Grid item xs={12} my={2}>
            <Typography variant="tagText">
              {
                "Seleccione la fuente de datos predeterminada para extraer la información del cálculo:"
              }
            </Typography>
          </Grid>

          <Controller
            name="source_app_calculation_info"
            control={control}
            key={JSON.stringify(watch("source_app_calculation_info"))}
            disabled={!!currentCalculation.id}
            defaultValue={isGenie ? "GENIE" : "AREA_EXE"}
            render={({ field }) => (
              <RadioGroup {...field} row sx={{ mt: 2 }}>
                <FormControlLabel
                  value="GENIE"
                  control={<Radio />}
                  label={TYPE_METHOD_IG_GT.GENIE}
                  disabled={!!currentCalculation.id}
                />
                <FormControlLabel
                  value="AREA_EXE"
                  control={<Radio />}
                  label={TYPE_METHOD_IG_GT.AREA_EXE}
                  disabled={!!currentCalculation.id}
                />
              </RadioGroup>
            )}
          />

          <Grid item xs={12} my={2}>
            <Typography variant="tagText">{"Datos a cumplimentar:"}</Typography>
          </Grid>

          <ResponsiveFormContainer columns={4}>
            <MAutocomplete
              key={JSON.stringify(getValues("laboratory"))}
              name="laboratory"
              options={laboratoriesList}
              label="Seleccione un Laboratorio"
              getOptionLabel={(option) =>
                `${option.codigo} - ${option.descripcion}` || ""
              }
              validation={{ required: "El laboratorio es requerido" }}
            />
            <MAutocomplete
              key={JSON.stringify(getValues("calculation_procedure"))}
              name="calculation_procedure"
              options={filteredCalculation}
              label="Procedimiento MLPMA"
              getOptionLabel={(option) => `${option.numop}` || ""}
            />
            {!isGenie && (
              <MTextField
                name="calculation_performance"
                label="Rendimiento"
                type="number"
                fullWidth
              />
            )}

            <MTextField
              key={JSON.stringify(
                getValues("calculation_self_absorption_factor")
              )}
              name="calculation_self_absorption_factor"
              label="Factor de autoabsorción"
              type="number"
              fullWidth
            />
            <MTextField
              key={JSON.stringify(watch("calculation_geometric_factor"))}
              name="calculation_geometric_factor"
              label="Factor geométrico"
              type="number"
              fullWidth
              disabled
            />
            <MDatePicker
              name="calculation_date"
              pickerType="datetime"
              label="Fecha Cálculo"
              // minDate={dayjs(watch("calculation_counting_date"))}
            />
          </ResponsiveFormContainer>

          <ResponsiveFormContainer columns={1} mt={1}>
            <MCheckbox
              key={JSON.stringify(getValues("calculation_type_report"))}
              name="calculation_type_report"
              options={typesReports}
              multiple={true}
              orientation="horizontal"
              columns={5}
              disabled={!!currentCalculation.id}
            />
          </ResponsiveFormContainer>

          <Grid item xs={12} my={2}>
            <Typography variant="tagText">{`${textTitle}`}</Typography>
          </Grid>
          {loadingFile && <Loading />}
          {isGenie && !loadingFile && (
            <>
              <FileUploadGenie />

              <Grid item xs={12} my={2}>
                <Typography variant="tagText">{"Datos obtenidos:"}</Typography>
              </Grid>
              <ResponsiveFormContainer columns={3}>
                <MTextField
                  key={JSON.stringify(getValues("calculation_equipment_file"))}
                  name="calculation_equipment_file"
                  label="Equipo"
                  disabled
                  fullWidth
                />
                <MTextField
                  key={JSON.stringify(
                    getValues("calculation_date_countig_file")
                  )}
                  name="calculation_date_countig_file"
                  label="Fecha contaje"
                  disabled
                  fullWidth
                />
                <MTextField
                  key={JSON.stringify(getValues("calculation_time_count"))}
                  name="calculation_time_count"
                  label="Tiempo total de contaje de la muestra (s)"
                  disabled
                  fullWidth
                />
                <MTextField
                  name="calculation_detector"
                  label="Geometría y detector"
                  disabled
                  fullWidth
                />
                <MTextField
                  key={JSON.stringify(getValues("calculation_geometry"))}
                  name="calibration_efficiency_date"
                  label="Fecha calibración eficiencias"
                  disabled
                  fullWidth
                />
                <MTextField
                  name="calibration_source_certificate"
                  label="Certificado fuente calibración"
                  disabled
                  fullWidth
                />
              </ResponsiveFormContainer>
              <ResponsiveFormContainer columns={1} mt={1}>
                <MTextField
                  name="calculation_background"
                  label="Fondo"
                  disabled
                  fullWidth
                />
              </ResponsiveFormContainer>
              <ResponsiveFormContainer columns={1} mt={1}>
                <MTextField
                  name="calculation_library"
                  label="Librería"
                  disabled
                  fullWidth
                />
              </ResponsiveFormContainer>
            </>
          )}
          {!isGenie && !loadingFile && (
            <>
              <Grid item xs={12} my={2}>
                <Typography variant="tagText">{"Fichero Muestra:"}</Typography>
              </Grid>
              <FileUploadAreaExe 
                name="source_app_calculation_info_file" 
                cod_instalacion={cod_instalacion}
                ref_cliente={ref_cliente}
              />
              <Grid item xs={12} my={2}>
                <Typography variant="tagText">{"Fichero Isotopos:"}</Typography>
              </Grid>
              <FileUploadAreaExe 
                name="source_app_calculation_info_file_2" 
                cod_instalacion={cod_instalacion}
                ref_cliente={ref_cliente}
              />
              <Grid item xs={12} my={2}>
                <Typography variant="tagText">{"Datos obtenidos:"}</Typography>
              </Grid>
              <ResponsiveFormContainer columns={3} mb={2}>
                <MTextField
                  key={JSON.stringify(
                    getValues("first_counting_time")
                  )}
                  name="first_counting_time"
                  label="Tiempo total de contaje"
                  disabled
                  fullWidth
                />
                <MTextField
                  key={JSON.stringify(
                    getValues("calculation_date_countig_file_exe")
                  )}
                  name="calculation_date_countig_file_exe"
                  label="Fecha de contaje"
                  disabled
                  fullWidth
                />
                <MTextField
                  key={JSON.stringify(getValues("pico_counts_341"))}
                  name="pico_counts_341"
                  label="Cuentas pico 351 Kev"
                  disabled
                  fullWidth
                />

                <MTextField
                  key={JSON.stringify(getValues("pico_counts_911"))}
                  name="pico_counts_911"
                  label="Cuentas pico 911 Kev"
                  disabled
                  fullWidth
                />
                <MTextField
                  key={JSON.stringify(getValues("detector_file"))}
                  name="detector_file"
                  label="Detector"
                  disabled
                  fullWidth
                />
              </ResponsiveFormContainer>
            </>
          )}
          {currentCalculation.id !== undefined && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: 2,
              }}
            >
              <MButton
                onClick={handleCalculate}
                text={textButtonCalculation}
                color="secondary"
                loading={loadingResults}
                disabled={!!watch("idResultPreload")}
              />
            </Box>
          )}

          {currentCalculation.id !== undefined && isGenie && <OutData />}
          {currentCalculation.id !== undefined && !isGenie && <OutDataAxe />}
        </>
      )}
    </>
  );
};

export default CalculationIG;
